<template>
  <div class="w-full grid" style="grid-template-columns: 320px 1fr;">
    <!-- LEFT SIDEBAR -->
    <div class="w-full flex flex-col p-3 border-r border-border-normal" style="min-height: calc(100vh - 139px)">
      <!-- Brand Logo and Name -->
      <div class="w-full flex items-center p-1">
        <div v-if="loadingBrand" class="skeleton w-8 h-8 rounded-md" />
        <img v-else :src="brand?.avatarUrl ?? firestoreBrand?.avatar" class="w-8 h-8 rounded-md">
        <div v-if="loadingBrand" class="skeleton ml-2.5 w-32 h-6 rounded-md" />
        <template v-else>
          <BaseText type="label" size="sm" class="text-text-muted pl-2.5 fade-in">
            {{ brand?.name ?? firestoreBrand?.name ?? '--' }}
          </BaseText>
          <VerifiedBadgeIcon class="ml-1 fade-in" />
        </template>
      </div>
      <!-- Brand Description -->
      <div v-if="loadingBrand" class="w-full flex flex-col gap-1 p-1 mt-1">
        <div v-for="i in 6" :key="`desc-skele-${i}`" class="skeleton w-full h-4 rounded-md" />
      </div>
      <BaseText v-else-if="brand?.enrichment?.description ?? brand?.description" type="body" size="sm" class="text-text-muted p-1 mt-1 fade-in">
        {{ brand?.enrichment?.description ?? brand?.description }}
      </BaseText>
      <!-- Monthly Traffic Preview -->
      <MonthlyTrafficPreview 
        class="mt-3"
        :page-id="firestoreBrand?.adLibraryId"
      />
      <!-- Extra Information Section -->
      <!-- Socials Preview -->
      <button v-if="socialsPreview" class="w-full flex items-center p-1.5 rounded-lg mt-3 mb-1
      transition-colors duration-100 hover:bg-neutral-25" @click="showSocialsPreviewModal = true">
        <SocialsIcon class="text-text-subdued" />
        <BaseText type="label" size="sm" class="text-text-muted px-2">
          Socials
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          {{ socialsPreview.total }}
        </BaseText>
        <div class="ml-auto flex items-center gap-1">
          <div v-for="platform in socialsPreview.platformPreviews" :key="`${platform.name}-icon`"
          class="w-5 h-5 p-0.5 flex items-center justify-center rounded overflow-hidden">
            <component :is="platform.icon" />
          </div>
          <BaseText v-if="socialsPreview.hiddenCount" type="label" size="xs"
          class="ml-1 py-0.5 px-1 rounded-md border border-border-normal bg-white">
            +{{ socialsPreview.hiddenCount }}
          </BaseText>
        </div>
      </button>
      <!-- divider -->
      <div v-if="showCategories" class="w-full h-0 border-b border-border-normal my-2" />
      <!-- Categories Section -->
      <div v-if="showCategories" class="w-full flex flex-col gap-2 py-2">
        <div class="w-full flex items-center p-1 gap-1.5">
          <BaseText type="label" size="sm" class="text-text-normal">
            Categories
          </BaseText>
          <BaseText type="label" size="xs" class="text-text-normal p-0.5 rounded-md bg-neutral-25">
            {{ brand?.enrichment?.categories?.length || '--' }}
          </BaseText>
        </div>
        <!-- Categories List -->
        <div class="categories-container overflow-hidden" :style="{ height: categoriesListHeight }">
          <div ref="categoriesList" class="w-full flex flex-wrap gap-1 min-h-0">
            <BaseText v-for="category in brand?.enrichment?.categories" :key="category" 
            type="label" size="xs" class="text-text-muted py-1 px-2 rounded-md border border-border-normal capitalize">
              {{ category }}
            </BaseText>
          </div>
        </div>
        <!-- 'See All' Button -->
        <button v-if="categoriesNaturalHeight > COLLAPSED_CATEGORY_HEIGHT" 
        class="self-start py-0.5 px-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-50"
        @click="showAllCategories = !showAllCategories">
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ showAllCategories ? 'See less' : 'See all' }}
          </BaseText>
        </button>
      </div>
      <!-- divider -->
      <div v-if="showDomains" class="w-full h-0 border-b border-border-normal my-2" />
      <!-- Domains Section -->
      <div v-if="showDomains" class="w-full flex flex-col gap-2 py-2">
        <div class="w-full flex items-center p-1 gap-1.5">
          <BaseText type="label" size="sm" class="text-text-normal">
            Domains
          </BaseText>
          <BaseText type="label" size="xs" class="text-text-normal p-0.5 rounded-md bg-neutral-25">
            {{ brand?.domains?.length || '--' }}
          </BaseText>
        </div>
        <!-- Domains List -->
        <div class="domains-list w-full flex flex-col gap-1 min-h-0 overflow-hidden"
        :style="{ height: domainsListHeight }">
          <a v-for="domain in brand?.domains" :key="domain.id" :href="`https://${domain.domain}`" target="_blank"
          class="w-full flex items-center p-1 rounded-md transition-colors duration-100 hover:bg-neutral-25">
            <LinkRoundedIcon class="text-icon-normal" />
            <BaseText type="body" size="sm" class="text-text-muted pl-1">
              {{ domain.domain }}
            </BaseText>
          </a>
        </div>
        <!-- 'See All' Button -->
        <button v-if="brand?.domains?.length > 3" class="self-start py-0.5 px-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-50"
        @click="showAllDomains = !showAllDomains">
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ showAllDomains ? 'See less' : 'See all' }}
          </BaseText>
        </button>
      </div>
      <!-- divider -->
      <div v-if="showSimilarBrands" class="w-full h-0 border-b border-border-normal my-2" />
      <!-- Similar Brands Section -->
      <div v-if="showSimilarBrands" class="w-full flex flex-col gap-2 py-2 fade-in">
        <BaseText type="label" size="sm" class="text-text-normal p-1">
          Similar Brands
        </BaseText>
        <SimilarBrandsPreview :similar-brands="similarBrands" />
      </div>
      <!-- No Data Available -->
      <div v-if="!loadingBrand && !brand?.id" class="w-full flex flex-col items-center justify-center" style="height: calc(100vh - 400px);">
        <div class="flex items-center gap-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 4.00098V7.60098M10 12.401V16.001M7.6 10.001H4M12.4 10.001H16M7.3 7.30098L6.4 6.40098M12.7 7.30098L13.6 6.40098M12.7 12.701L13.6 13.601M7.3 12.701L6.4 13.601" 
            stroke="#303546" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <BaseText type="label" size="sm" class="text-text-normal">
            No Enrichment Data
          </BaseText>
        </div>
        <BaseText type="body" size="sm" class="text-text-normal mt-3 text-center">
          This brand has not been picked up by our enrichment processor yet. Please try again later or contact support.
        </BaseText>
        <button class="mt-6 flex items-center gap-1.5 px-1.5 py-1 rounded-md transition-colors duration-100 hover:bg-neutral-25"
        @click="$emit('viewMetaAds')">
          <MonoMetaIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-muted">
            View Meta Ads
          </BaseText>
        </button>
      </div>
    </div>
    <!-- CONTENT AREA -->
    <div class="w-full flex flex-col gap-4 px-4 pt-4 pb-16">
      <!-- Top Ranked Ads Preview -->
      <AdRankPreview 
        :page-id="firestoreBrand?.adLibraryId"
        @openAdDetails="showAdvertisementDetails"
        @analyzeAdRanks="$emit('analyzeAdRanks')"
      />
      <!-- Creative Velocity -->
      <CreativeVelocity 
        :page-id="firestoreBrand?.adLibraryId"
      />
      <!-- Creator Partnerships Preview -->
      <CreatorPartnershipsPreview 
        :page-id="firestoreBrand?.adLibraryId"
        @viewPartnerAds="$emit('viewPartnerAds')"
      />
    </div>
    <!-- Advertisement Details Drawer -->
    <AdvertisementDetailsDrawer
      v-if="showAdvertisementDetailsDrawer"
      :advertisement="selectedAdvertisement"
      discovery
      @close="showAdvertisementDetailsDrawer = false"
    />
    <!-- Modals -->
    <SocialsPreviewModal
      v-if="showSocialsPreviewModal"
      :socials="normalizedSocials"
      @close="showSocialsPreviewModal = false"
    />
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import { getSocialPlatformIcon, getSocialPlatformName, normalizeSocialsForDisplay } from '@/utils/spyder/brandSocialsUtils'

// Components
import AdRankPreview from './AdRankPreview.vue'
import CreativeVelocity from './creativeVelocity/CreativeVelocity.vue'
import CreatorPartnershipsPreview from './CreatorPartnershipsPreview.vue'
import MonthlyTrafficPreview from './MonthlyTrafficPreview.vue'
import SimilarBrandsPreview from './SimilarBrandsPreview.vue'
import SocialsPreviewModal from './SocialsPreviewModal.vue'
import AdvertisementDetailsDrawer from '../../advertisements/AdvertisementDetailsDrawer.vue'

// Icons
import VerifiedBadgeIcon from '../../globals/Icons/AgencyIcons/VerifiedBadgeIcon.vue'
import LinkRoundedIcon from '../../globals/Icons/LinkRoundedIcon.vue'
import SocialsIcon from '../../globals/Icons/SocialsIcon.vue'
import MonoMetaIcon from '../../globals/Icons/socials/mono/MonoMetaIcon.vue'

const NUM_COLLAPSED_CATEGORY_ROWS = 4
const COLLAPSED_CATEGORY_HEIGHT = NUM_COLLAPSED_CATEGORY_ROWS * 26 + (NUM_COLLAPSED_CATEGORY_ROWS - 1) * 4

export default {
  name: 'BrandOverviewView',
  components: {
    AdRankPreview,
    CreativeVelocity,
    CreatorPartnershipsPreview,
    MonthlyTrafficPreview,
    SimilarBrandsPreview,
    SocialsPreviewModal,
    AdvertisementDetailsDrawer,
    VerifiedBadgeIcon,
    LinkRoundedIcon,
    AdvertisementDetailsDrawer,
    SocialsIcon,
    MonoMetaIcon
  },
  props: {
    // Temp until we find a more sensible refactor
    firestoreBrand: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      brand: {},
      similarBrands: [],
      
      // Ad Details
      selectedAdvertisement: null,
      showAdvertisementDetailsDrawer: false,

      // UI State
      loadingBrand: true,
      loadingSimilarBrands: true,
      showSocialsPreviewModal: false,
      showAllDomains: false,
      showAllCategories: false,
      categoriesNaturalHeight: 0,
      COLLAPSED_CATEGORY_HEIGHT
    }
  },
  watch: {
    firestoreBrand: {
      handler (brand) {
        if (!brand) return
        this.fetchPageData()
      },
      immediate: true
    },
    'brand.enrichment.categories': {
      handler (value) {
        if (!value?.length) return
        this.updateCategoriesListHeight()
      },
      deep: true
    }
  },
  computed: {
    showCategories () {
      return this.brand?.enrichment?.categories?.length
    },
    showDomains () {
      return this.brand?.domains?.length
    },
    showSimilarBrands () {
      return !this.loadingSimilarBrands && this.similarBrands?.length
    },
    normalizedSocials () {
      if (!this.brand?.socials) return null
      return normalizeSocialsForDisplay(this.brand.socials)
    },
    socialsPreview () {
      if (!this.normalizedSocials?.length) return null
      const totalSocials = this.normalizedSocials.length
      const uniquePlatformSocials = [...new Set(this.normalizedSocials.map(social => social.network))]
      return {
        total: totalSocials,
        platformPreviews: uniquePlatformSocials.slice(0, 3).map(network => ({
          name: getSocialPlatformName(network),
          icon: getSocialPlatformIcon(network),
        })),
        hiddenCount: Math.max(0, uniquePlatformSocials.length - 3),
      }
    },
    domainsListHeight () {
      if (!this.showDomains) return '0px'
      const domainsCount = this.brand?.domains?.length
      const naturalHeight = domainsCount * 28 + (domainsCount - 1) * 4
      return this.showAllDomains
        ? `${naturalHeight}px`
        : `${Math.min(naturalHeight, 92)}px`
    },
    categoriesListHeight () {
      if (!this.showCategories) return '0px'
      return this.showAllCategories
        ? `${this.categoriesNaturalHeight}px`
        : `${Math.min(this.categoriesNaturalHeight, COLLAPSED_CATEGORY_HEIGHT)}px`
    }
  },
  methods: {
    async fetchPageData () {
      this.fetchBrand()
      this.fetchSimilarBrands()
    },
    async fetchBrand () {
      if (!this.firestoreBrand?.adLibraryId) return
      this.loadingBrand = true
      try {
        this.brand = await ForeplayAPI.Brands.getByPageId(this.firestoreBrand.adLibraryId)
      } catch (error) {
        console.error('Error fetching brand:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch brand. See console for details.'
        })
      } finally {
        this.loadingBrand = false
      }
    },
    async fetchSimilarBrands () {
      if (!this.firestoreBrand?.adLibraryId) return
      this.loadingSimilarBrands = true
      try {
        const brandResults = await ForeplayAPI.Brands.findSimilarBrands(this.firestoreBrand.adLibraryId)
        // Filter for only enriched brands
        this.similarBrands = brandResults.filter(brand => brand.enrichment?.description)
      } catch (error) {
        console.error('Error fetching similar brands:', error)
      } finally {
        this.loadingSimilarBrands = false
      }
    },
    showAdvertisementDetails (advertisement) {
      this.selectedAdvertisement = advertisement
      this.showAdvertisementDetailsDrawer = true
    },
    updateCategoriesListHeight () {
      if (!this.showCategories) return
      this.$nextTick(() => {
        const categoriesList = this.$refs.categoriesList
        if (!categoriesList) return
        this.categoriesNaturalHeight = categoriesList.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.domains-list, .categories-container {
  transition: height 200ms ease-in-out;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>